import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { RevealLines, FadeUp } from "@/components/site/Reveal";
import { CONTACT } from "@/data/site";
import { formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const bannerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const bannerY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous vous répondons sous 24h.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-ink/20 py-3 text-lg outline-none transition-colors focus:border-violet placeholder:text-muted-foreground/60";

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-40 lg:pt-52 pb-16">
        <p className="mb-8 flex items-center gap-3 text-sm tracking-[0.25em] uppercase text-muted-foreground">
          <span className="h-px w-10 bg-violet" /> Contact
        </p>
        <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-8xl">
          <RevealLines lines={["Démarrons", "le projet."]} />
        </h1>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pb-28 grid lg:grid-cols-2 gap-16 lg:gap-24">
        {/* LEFT — coordonnées */}
        <div>
          <FadeUp>
            <p className="text-sm tracking-[0.25em] uppercase text-muted-foreground mb-4">Par téléphone</p>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl hover:text-violet transition-colors block"
              data-testid="contact-phone"
            >
              {CONTACT.phone}
            </a>
            <p className="mt-3 flex items-center gap-2 text-muted-foreground">
              <Phone size={16} /> {CONTACT.hours}
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <a
              href={`https://wa.me/${CONTACT.whatsapp.replace("+", "")}`}
              target="_blank"
              rel="noreferrer"
              className="mt-12 flex items-center justify-between gap-4 border border-ink/15 p-6 hover:border-violet hover:bg-violet hover:text-white transition-colors duration-300 group"
              data-testid="contact-whatsapp"
            >
              <div className="flex items-center gap-4">
                <MessageCircle size={28} />
                <div>
                  <div className="font-display font-bold text-xl">WhatsApp</div>
                  <div className="text-sm opacity-70">24h/24, 7j/7</div>
                </div>
              </div>
              <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </FadeUp>

          <FadeUp delay={0.2}>
            <div className="mt-12 flex items-start gap-4">
              <MapPin className="text-violet shrink-0 mt-1" />
              <div>
                <div className="font-display font-bold text-xl">Equinoxe Studio</div>
                <p className="text-muted-foreground">
                  {CONTACT.city} ({CONTACT.postal}) — Lot & départements limitrophes.
                  <br />
                  Prestations digitales : France entière.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* RIGHT — form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="space-y-8"
          data-testid="contact-form"
        >
          <div>
            <label className="text-sm text-muted-foreground">Nom *</label>
            <input className={field} value={form.name} onChange={set("name")} placeholder="Votre nom" data-testid="contact-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="text-sm text-muted-foreground">Email *</label>
              <input type="email" className={field} value={form.email} onChange={set("email")} placeholder="vous@email.fr" data-testid="contact-email" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Téléphone</label>
              <input className={field} value={form.phone} onChange={set("phone")} placeholder="06 …" data-testid="contact-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Sujet</label>
            <input className={field} value={form.subject} onChange={set("subject")} placeholder="Site web, branding, marquage…" data-testid="contact-subject" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Message *</label>
            <textarea rows={5} className={field + " resize-none"} value={form.message} onChange={set("message")} placeholder="Parlez-nous de votre projet…" data-testid="contact-message" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 hover:bg-violet transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
            {sending ? "Envoi…" : "Envoyer le message"}
          </button>
        </motion.form>
      </section>

      {/* BANNER */}
      <section
        ref={bannerRef}
        className="relative h-[60vh] min-h-[400px] overflow-hidden bg-ink"
        data-testid="contact-banner"
      >
        <motion.img
          style={{ y: bannerY }}
          src="https://customer-assets-cm19k8pv.emergentagent.net/job_equinoxe-lot/artifacts/cd789fb08b5241e2_Duo%20%C3%A9l%C3%A9gant%20sous%20le%20signe%20violet.png"
          alt="Equinoxe Studio — contactez-nous"
          className="absolute inset-0 h-[120%] w-full object-cover object-center -top-[10%]"
          draggable="false"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-12 pb-14 lg:pb-20">
            <FadeUp>
              <p className="text-sm tracking-[0.25em] uppercase text-white/60 mb-4">À votre écoute</p>
              <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.9] max-w-4xl">
                Un projet en tête ?<br />
                <span className="font-editorial italic font-normal text-violet-light">Appelons-nous.</span>
              </h2>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
